import { useEffect } from "react";
import { useHarmonicIntervalFn, useUpdate } from "react-use";
import { TimerConfigSchema } from "shared/datamodel/schemas/timer";
import { SyncService } from "../services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useTimerData } from "frontend/subscriptions";
import tracking from "frontend/tracking";
import consts from "shared/consts";

export type CanvasTimer = ReturnType<typeof useTimer>;

export function useTimer(syncService: SyncService<RW> | null) {
  const timerDefault: TimerConfigSchema = {
    start: Date.now(),
    end: Date.now() + 300 * 1000,
    pause: 0,
    over: 0,
    isRunning: false,
  };

  const timer: TimerConfigSchema = useTimerData(syncService?.getReplicache()) ?? timerDefault;
  const timerOver = timer.end <= Date.now() && timer.isRunning;
  const hideTimer = timer.over !== 0 && timer.over + 5000 <= Date.now();

  useEffect(() => {
    timerOver && syncService?.mutate.putTimer({ ...timerDefault, over: Date.now() });
  }, [timerOver]);

  useEffect(() => {
    hideTimer && syncService?.mutate.putTimer(timerDefault);
  }, [hideTimer]);

  const forceRefresh = useUpdate();
  useHarmonicIntervalFn(forceRefresh, timer.isRunning || timer.over ? 1000 : null);

  function set(seconds: number) {
    syncService?.mutate.putTimer({
      start: Date.now(),
      end: Date.now() + seconds * 1000,
      pause: 0,
      over: 0,
      isRunning: false,
    });
  }

  function getSecondsLeft() {
    const { isRunning, pause, end, start } = timer;

    if (isRunning) {
      return (end - Date.now()) / 1000;
    }
    if (pause === 0) {
      return (end - start) / 1000;
    }
    return (end - pause) / 1000;
  }

  function start() {
    if (timer.pause) {
      return resume();
    } else {
      tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "start-timer", "play-button");
    }
    syncService?.mutate.putTimer({
      start: Date.now(),
      end: Date.now() + timer.end - timer.start,
      pause: 0,
      over: 0,
      isRunning: true,
    });
  }

  function pause() {
    timer.isRunning && syncService?.mutate.putTimer({ ...timer, pause: Date.now(), isRunning: false });
  }

  function resume() {
    !timer.isRunning &&
      syncService?.mutate.putTimer({
        start: timer.start + Date.now() - timer.pause,
        end: timer.end + Date.now() - timer.pause,
        pause: 0,
        over: 0,
        isRunning: true,
      });
  }

  function reset() {
    timer.over
      ? syncService?.mutate.putTimer({
          start: Date.now(),
          end: Date.now() + 300 * 1000,
          pause: 0,
          over: timer.over,
          isRunning: false,
        })
      : syncService?.mutate.putTimer(timerDefault);
  }

  function increment() {
    if (timer.end - Date.now() < 99 * 60 * 1000) {
      syncService?.mutate.putTimer({
        ...timer,
        end: timer.end + 60 * 1000,
      });
    }
  }

  function decrement() {
    if (timer.end - Date.now() > 60 * 1000) {
      syncService?.mutate.putTimer({
        ...timer,
        end: timer.end - 60 * 1000,
      });
    }
  }

  function getProgress() {
    const totalTime = (timer.end - timer.start) / 1000;
    const secondsLeft = getSecondsLeft();
    const percent = secondsLeft / totalTime;
    return (1 - percent) * 100;
  }

  const secondsLeft = getSecondsLeft();
  const round = Math.ceil(secondsLeft);
  const minutesString = Math.floor(round / 60)
    .toString()
    .padStart(2, "0");
  const secondsString = (round % 60).toString().padStart(2, "0");
  return {
    config: timer,
    secondsLeft: getSecondsLeft(),
    minutesString,
    secondsString,
    set,
    start,
    pause,
    resume,
    reset,
    increment,
    decrement,
    progress: getProgress(),
  };
}
